import { useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

/**
 * Branded 404 page.
 *
 * The SPA cannot change the HTTP status that the static host already sent
 * (the host returns the SPA shell with 200). The actual HTTP 404 must be
 * emitted by the production static server for unknown routes
 * (see public/_headers and public/_redirects, and the "flagged for founder"
 * note in the SEO changelog).
 *
 * We still render a branded 404 so visitors and crawlers that reach this
 * route see a correct, differentiated page (not a redirect to the homepage)
 * and so prerender-style crawlers that honour the status-code hint below
 * record a 404.
 */
export default function NotFound() {
  useEffect(() => {
    document.title = "Page not found — Zynthoro";

    // Prerender/SPA status-code hints honoured by some crawler toolchains
    // (Prerender.io, Rendertron). Harmless on a real browser.
    const addMeta = (name, content, id) => {
      let el = document.getElementById(id);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        el.id = id;
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    addMeta("prerender-status-code", "404", "zy-prerender-status");
    addMeta("robots", "noindex, nofollow", "zy-notfound-robots");

    return () => {
      document.getElementById("zy-prerender-status")?.remove();
      document.getElementById("zy-notfound-robots")?.remove();
    };
  }, []);

  return (
    <>
      <Navbar />
      <section
        style={{ background: "var(--zy-blue)" }}
        className="text-white"
        data-testid="notfound-hero"
      >
        <div className="zy-container py-20 md:py-28 max-w-[720px]">
          <p
            className="uppercase tracking-[0.18em] text-[12px] mb-3"
            style={{ color: "var(--zy-gold)" }}
          >
            404 · Page not found
          </p>
          <h1
            data-testid="notfound-title"
            className="text-4xl md:text-5xl font-bold tracking-tight leading-[1.15]"
          >
            This page doesn&rsquo;t exist.
          </h1>
          <p className="mt-5 text-white/75 text-[15.5px] leading-relaxed">
            The link may be broken or the page may have moved. Try one of the
            routes below, or head back to the Zynthoro homepage.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              data-testid="notfound-home-link"
              className="inline-flex items-center rounded-full bg-white text-[var(--zy-blue)] px-5 py-2.5 text-[14px] font-semibold hover:bg-white/90 transition"
            >
              Go to homepage
            </Link>
            <Link
              to="/blog"
              data-testid="notfound-blog-link"
              className="inline-flex items-center rounded-full ring-1 ring-white/40 text-white px-5 py-2.5 text-[14px] font-semibold hover:bg-white/10 transition"
            >
              Read the blog
            </Link>
            <Link
              to="/pricing"
              data-testid="notfound-pricing-link"
              className="inline-flex items-center rounded-full ring-1 ring-white/40 text-white px-5 py-2.5 text-[14px] font-semibold hover:bg-white/10 transition"
            >
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="zy-container py-14 md:py-20 max-w-[860px]">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#0A1628]">
            Popular places to continue
          </h2>
          <ul className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4 text-[15px]">
            <li>
              <Link
                to="/modules"
                className="block rounded-xl ring-1 ring-black/[0.08] p-4 hover:ring-[var(--zy-blue)]/40 transition"
              >
                <span className="block font-semibold text-[#0A1628]">
                  12 connected modules
                </span>
                <span className="block text-black/60 mt-1 text-[13.5px]">
                  Finance, Sales, HR, Projects, Planning, Operations and more.
                </span>
              </Link>
            </li>
            <li>
              <Link
                to="/assistants"
                className="block rounded-xl ring-1 ring-black/[0.08] p-4 hover:ring-[var(--zy-blue)]/40 transition"
              >
                <span className="block font-semibold text-[#0A1628]">
                  Four AI specialists
                </span>
                <span className="block text-black/60 mt-1 text-[13.5px]">
                  Zyntha, Thoro, Zyona and Zynthoro Assist.
                </span>
              </Link>
            </li>
            <li>
              <Link
                to="/pricing"
                className="block rounded-xl ring-1 ring-black/[0.08] p-4 hover:ring-[var(--zy-blue)]/40 transition"
              >
                <span className="block font-semibold text-[#0A1628]">
                  Pricing
                </span>
                <span className="block text-black/60 mt-1 text-[13.5px]">
                  Kickstart from &euro;79, subscriptions from &euro;499/month.
                </span>
              </Link>
            </li>
            <li>
              <Link
                to="/about"
                className="block rounded-xl ring-1 ring-black/[0.08] p-4 hover:ring-[var(--zy-blue)]/40 transition"
              >
                <span className="block font-semibold text-[#0A1628]">
                  About Zynthoro
                </span>
                <span className="block text-black/60 mt-1 text-[13.5px]">
                  The company and the founder behind the platform.
                </span>
              </Link>
            </li>
          </ul>
        </div>
      </section>

      <Footer />
    </>
  );
}
